import { useState } from 'react';
import { TRACKERS, clearConsent, consentIsRequired } from '../lib/consent.js';
import { LEGAL_NOTICE, PRIVACY, incompleteLegalFields } from '../lib/legal-notice.js';

/**
 * The legal footer.
 *
 * Three obligations meet here, and each is a separate rule:
 *
 *   * **Mentions légales** — LCEN article 6 III. An online service must
 *     identify its publisher, its host and how to reach a person.
 *   * **Information of the data subject** — RGPD articles 13 and 14. The
 *     information must be accessible from where the data is collected, not
 *     buried in a contract.
 *   * **Withdrawing consent must be as easy as giving it** — article 82 of the
 *     loi Informatique et Libertés. That is what "Gérer les cookies" is for,
 *     and it is only shown when there is something to manage.
 *
 * The identity below is filled from `legal-notice.ts`. It is deliberately not
 * invented here: a mentions légales block with a plausible-looking but wrong
 * SIREN is worse than an obviously incomplete one, because nobody notices it.
 */

type Panel = 'legal' | 'privacy' | 'cookies' | null;

export function LegalFooter({
  tone = 'light',
}: {
  /** `dark` for the site's violet footer. The obligations do not change. */
  readonly tone?: 'light' | 'dark';
} = {}): React.JSX.Element {
  const [panel, setPanel] = useState<Panel>(null);
  const [reopen, setReopen] = useState(0);

  return (
    <footer
      className={
        tone === 'dark'
          ? 'text-caption text-ink-light-muted'
          : 'mt-12 border-t border-line pt-6 text-caption text-ink-muted'
      }
    >
      <nav className="flex flex-wrap gap-x-4 gap-y-2">
        <button
          type="button"
          className="underline"
          onClick={() => {
            setPanel('legal');
          }}
        >
          Mentions légales
        </button>
        <button
          type="button"
          className="underline"
          onClick={() => {
            setPanel('privacy');
          }}
        >
          Données personnelles
        </button>
        <button
          type="button"
          className="underline"
          onClick={() => {
            setPanel('cookies');
          }}
        >
          Cookies
        </button>
        {/*
          Only when there is a decision to revisit. A "manage cookies" link on a
          site that sets none is an invitation to look for something that does
          not exist.
        */}
        {consentIsRequired(TRACKERS) && (
          <button
            type="button"
            className="underline"
            onClick={() => {
              clearConsent();
              setReopen(reopen + 1);
            }}
          >
            Gérer les cookies
          </button>
        )}
      </nav>

      {panel !== null && (
        <div className="mt-4 space-y-2 rounded-control border border-line bg-surface p-4 text-ink">
          {panel === 'legal' && <LegalNotice />}
          {panel === 'privacy' && <PrivacySummary />}
          {panel === 'cookies' && <CookieStatement />}
          <button
            type="button"
            className="underline"
            onClick={() => {
              setPanel(null);
            }}
          >
            Fermer
          </button>
        </div>
      )}
    </footer>
  );
}

function Field({
  label,
  value,
}: {
  readonly label: string;
  readonly value: string;
}): React.JSX.Element {
  return (
    <p>
      <span className="font-medium text-ink">{label} : </span>
      {value}
    </p>
  );
}

function LegalNotice(): React.JSX.Element {
  const missing = incompleteLegalFields();

  return (
    <div className="space-y-1">
      <h2 className="text-ui-sm font-semibold text-ink">Mentions légales</h2>
      <Field label="Éditeur" value={LEGAL_NOTICE.publisher} />
      <Field label="Forme juridique" value={LEGAL_NOTICE.legalForm} />
      {/*
        The share capital was in `legal-notice.ts` and in the list of fields the
        notice refuses to be complete without — and was never printed. It would
        have been filled in and still not published, which is the one failure
        mode `incompleteLegalFields` cannot catch: it checks the value, not
        whether anything renders it.
      */}
      <Field label="Capital social" value={LEGAL_NOTICE.shareCapital} />
      <Field label="Siège social" value={LEGAL_NOTICE.address} />
      <Field label="SIREN" value={LEGAL_NOTICE.siren} />
      <Field label="SIRET (siège)" value={LEGAL_NOTICE.siret} />
      <Field label="Immatriculation" value={LEGAL_NOTICE.rcs} />
      <Field label="TVA intracommunautaire" value={LEGAL_NOTICE.vatNumber} />
      <Field label="Directeur de la publication" value={LEGAL_NOTICE.publicationDirector} />
      <Field label="Contact" value={LEGAL_NOTICE.contactEmail} />
      <Field label="Hébergeur" value={LEGAL_NOTICE.host} />
      {missing.length > 0 && (
        // Visible rather than silent. An incomplete notice that looks complete
        // is the failure mode worth avoiding.
        <p className="mt-2 rounded border border-amber-300 bg-amber-50 p-2 text-amber-900">
          Informations à compléter avant mise en ligne : {missing.join(', ')}.
        </p>
      )}
    </div>
  );
}

function PrivacySummary(): React.JSX.Element {
  return (
    <div className="space-y-2">
      <h2 className="text-ui-sm font-semibold text-ink">Données personnelles</h2>
      <p>
        GSI Voice répond au téléphone pour le compte de votre établissement. Pour les données des
        personnes qui appellent, <strong>l’établissement est responsable de traitement</strong> et{' '}
        {LEGAL_NOTICE.publisher} est <strong>sous-traitant</strong> au sens de l’article 28 du RGPD.
        Pour les données de votre propre compte (identité, facturation), {LEGAL_NOTICE.publisher}{' '}
        est responsable de traitement.
      </p>
      <p>
        <span className="font-medium text-ink">Données traitées : </span>
        numéro de téléphone de l’appelant, objet de l’appel, rendez-vous pris, et — uniquement si
        l’établissement l’active — un résumé de la conversation. L’enregistrement audio est{' '}
        <strong>désactivé par défaut</strong>.
      </p>
      <p>
        <span className="font-medium text-ink">Conservation : </span>
        les résumés d’appel suivent la durée choisie par l’établissement (
        {PRIVACY.transcriptDefault} par défaut). Les fiches client sont effacées{' '}
        {PRIVACY.customerDefault} après le dernier contact. Ces durées sont appliquées
        automatiquement.
      </p>
      <p>
        <span className="font-medium text-ink">Vos droits : </span>
        accès, rectification, effacement, limitation, opposition et portabilité. Écrivez à{' '}
        {LEGAL_NOTICE.privacyEmail}. Vous pouvez introduire une réclamation auprès de la CNIL.
      </p>
      <p>
        <span className="font-medium text-ink">Sécurité : </span>
        les coordonnées sont chiffrées en base, les clés ne sont jamais stockées avec les données,
        et chaque établissement est isolé des autres au niveau de la base de données.
      </p>
    </div>
  );
}

function CookieStatement(): React.JSX.Element {
  if (!consentIsRequired(TRACKERS)) {
    return (
      <div className="space-y-2">
        <h2 className="text-ui-sm font-semibold text-ink">Cookies</h2>
        <p>
          <strong>Ce service n’utilise aucun traceur.</strong> Pas de mesure d’audience, pas de
          publicité, aucun script tiers, aucun cookie déposé par un tiers.
        </p>
        <p>
          Un seul cookie existe, et seulement une fois que vous êtes connecté : il porte votre
          session. Il est illisible par le code de la page, il n’est envoyé qu’à ce site, et il
          disparaît lorsque vous vous déconnectez. Votre jeton d’accès, lui, reste en mémoire dans
          l’onglet et disparaît lorsque vous le fermez : il n’est écrit nulle part.
        </p>
        <p>
          C’est pourquoi aucune bannière ne vous est présentée : l’article 82 de la loi Informatique
          et Libertés n’exige un consentement que pour ce qui n’est pas strictement nécessaire au
          service demandé. Un cookie qui vous maintient connecté en est l’exemple même, et nous
          n’écrivons rien d’autre.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <h2 className="text-ui-sm font-semibold text-ink">Cookies</h2>
      <ul className="list-disc space-y-1 pl-5">
        {TRACKERS.map((tracker) => (
          <li key={tracker.id}>
            <span className="font-medium text-ink">{tracker.id}</span> — {tracker.purpose}{' '}
            (destinataire : {tracker.recipient}, durée : {tracker.lifetime})
          </li>
        ))}
      </ul>
      <p>Vous pouvez revenir sur votre choix à tout moment avec « Gérer les cookies ».</p>
    </div>
  );
}
